import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  Button,
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid
} from '@mui/material'
import { ValidatorForm } from 'react-material-ui-form-validator'
import { Brand, IEWarningPopup, Alert } from 'frame/components'
import { PasswordIndicator } from 'features/account/components'
import MuiTextfield from 'frame/components/mui-textfield'
// import { useNavigate } from 'react-router-dom'
import * as fromAuth from 'auth'
import { themeV5 } from 'theme/v5'

const Verification = ({ onVerify, onClear, verification }) => {
  const form = useRef()
  const location = useLocation()
  const rawToken = location.search || ''
  // const navigate = useNavigate()
  const disabled = false

  const verifiedSuccess = verification && verification.success && verification.success
  const responseMessage = verification && verification.responseMessage

  // if(verifiedSuccess) {
  //   navigate('/dashboard')
  // }

  useEffect(() => {
    onClear() // clear any previous verification messages onload
  }, [onClear])

  // redirect from page if verification url expired
  let token = ''
  if (rawToken !== '') {
    token = rawToken.split('=')[1]
    // console.log('token', token)
    if (token.startsWith('=3D') && window.location.href.startsWith('http://localhost:')) {
      token = token.slice(2)
    }
    // console.log('token sliced2', token)
  }

  const isIE = /*@cc_on!@*/false || !!document.documentMode

  const [values, setValues] = useState({
    password: '',
    confirmPassword: '',
  })

  const hasNumber = (value) => /(.*\d.*)/.test(value)
  const hasLowercase = (value) => /(.*[a-z].*)/.test(value)
  const hasUppercase = (value) => /(.*[A-Z].*)/.test(value)
  const hasSymbol = (value) => /[-!$%^&@#*()_+|~=`{}[\]:";'<>?,./]/.test(value)

  useEffect(() => {

    ValidatorForm.addValidationRule('isPasswordMatch', (value) => {
      return value !== values.password ? false : true
    })

    ValidatorForm.addValidationRule('hasNumber', (value) => hasNumber(value))
    ValidatorForm.addValidationRule('hasLowercase', (value) => hasLowercase(value))
    ValidatorForm.addValidationRule('hasUppercase', (value) => hasUppercase(value))
    ValidatorForm.addValidationRule('hasSymbol', (value) => hasSymbol(value))

    return () => {
      ValidatorForm.removeValidationRule('isPasswordMatch')
      ValidatorForm.removeValidationRule('hasNumber')
      ValidatorForm.removeValidationRule('hasLowercase')
      ValidatorForm.removeValidationRule('hasUppercase')
      ValidatorForm.removeValidationRule('hasSymbol')
    }

  }, [values.password])

  const onChange = (key) => ({ target: { value } }) =>
    setValues({
      ...values,
      [key]: value
    })

  const valid = value => value && value !== ''
  const disableSave = ! (valid(values.password)
    && valid(values.confirmPassword)
    && values.password === values.confirmPassword
    && values.password.length > 7
    && hasNumber(values.password)
    && hasLowercase(values.password)
    && hasUppercase(values.password)
    && hasSymbol(values.password))

  const onVerifyHandler = () => {
    if (valid(token) && valid(values.confirmPassword))
    {onVerify({
      password: values.confirmPassword,
      token
    })}
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingY: '48px',
        paddingX: '16px',
      }}
      title="Login">
      {isIE && <IEWarningPopup />}
      <Card
        sx={{
          width: '960px',
          maxWidth: '100%',
          overflow: 'unset',
          display: 'flex',
          position: 'relative',
          '& > *': {
            flexGrow: 1,
            flexBasis: '50%',
            width: '50%',
          },
          '@media (max-width: 760px)': {
            maxWidth: '400px',
          },
        }}>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: [3, 4],
            minHeight: 300,
          }}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                paddingBottom: '24px',
                color: themeV5.palette.white,
              }}>
              <Brand />
            </Box>
            {/* {JSON.stringify(values)} */}

            <Typography gutterBottom variant="body1">
              Please enter your password and click verify account to complete your account verification.
            </Typography>

            {/* token: {JSON.stringify(token)} */}

            <ValidatorForm
              autoComplete="off"
              noValidate
              ref={form}
              onSubmit={() => console.log('ValidatorFormonSubmit clicked')}>
              <Grid paddingTop={4}>
                <Grid xs={12} paddingBottom={2}>
                  <MuiTextfield
                    id="password"
                    disabled={disabled}
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={values.password}
                    onChange={onChange('password')}/>
                </Grid>
                <Grid xs={12} spacing={2} paddingBottom={4}>
                  <MuiTextfield
                    id="confirmPassword"
                    disabled={disabled}
                    fullWidth
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    value={values.confirmPassword}
                    onChange={onChange('confirmPassword')}/>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">Password Policy:</Typography>
                  <PasswordIndicator
                    passwordMatch={values.password !== '' && values.password === values.confirmPassword}
                    meetsPasswordLength={values.password.length > 7}
                    hasNumber={hasNumber(values.password)}
                    hasLowercase={hasLowercase(values.password)}
                    hasUppercase={hasUppercase(values.password)}
                    hasSymbol={hasSymbol(values.password)}/>
                </Grid>
              </Grid>
            </ValidatorForm>

          </Box>
          <Button
            sx={{
              marginTop: '24px',
              width: '100%',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
            fullWidth
            color="error"
            disabled={disableSave}
            type="submit"
            variant="contained"
            onClick={onVerifyHandler}>
            Verify Account
          </Button>
          <br />
          {!verifiedSuccess && responseMessage && (
            <Alert>
              {responseMessage}
              {
                responseMessage.toLowerCase().includes('already verified') &&
                <a href="/dashboard">Click here to login or reset your password</a>
              }
            </Alert>
          )}
        </CardContent>
        <CardMedia
          sx={{
            position: 'relative',
            borderTopRightRadius: '32px',
            borderBottomRightRadius: '32px',
            padding: 3,
            color: themeV5.palette.white,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            '@media (max-width: 760px)': {
              display: 'none',
            },
          }}
          image="/static/images/login-image.jpg"
          title="Toyota">
        </CardMedia>
      </Card>
    </Box>
  )
}

Verification.propTypes = {
  onVerify: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  verification: PropTypes.object.isRequired,
}

const mapStateToProps = ({ account }) => ({
  verification: account.verification,
})

const mapDispatchToProps = (dispatch) => ({
  onVerify: (payload) =>
    dispatch(fromAuth.verifyUser(payload)),
  onClear: () =>
    dispatch(fromAuth.verifyUserClearMessage())
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Verification)
